import React from 'react';

/**
 * Icons that repeat many times per page. Glyphs with one colour (footer markers, menu arrows,
 * change carets) are a CSS-masked span (classes in globals.css), one element each instead of
 * svg + path. Icons that need their own fill / stroke (rating stars, coin fallback) are
 * `<use>` references to the symbols below, so the path data is sent once. `<SvgSprite />` is
 * rendered once in the (site) layout. Add an icon: a `<symbol id='ks-…'>` here (or a mask class
 * in globals.css and an entry in MASKED) and its name in `SpriteIconName`.
 */
export type SpriteIconName =
    | 'chevron-left'
    | 'chevron-down'
    | 'star'
    | 'caret-up'
    | 'arrow-left-s'
    | 'coins';

export const SvgSprite: React.FC = () => (
    <svg
        aria-hidden='true'
        focusable='false'
        width='0'
        height='0'
        style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}
    >
        <defs>
            <symbol id='ks-star' viewBox='0 0 24 24'>
                <path d='M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z' />
            </symbol>
            <symbol id='ks-coins' viewBox='0 0 24 24'>
                <circle cx='8' cy='8' r='6' />
                <path d='M18.09 10.37A6 6 0 1 1 10.34 18' />
                <path d='M7 6h1v4' />
                <path d='m16.71 13.88.7.71-2.82 2.82' />
            </symbol>
        </defs>
    </svg>
);

// Plain glyphs repeat dozens of times per page: a CSS-masked span (classes in globals.css) is one
// element instead of svg + path. Icons with their own fill / stroke colours use the sprite.
const MASKED = new Set<SpriteIconName>([
    'chevron-left',
    'chevron-down',
    'caret-up',
    'arrow-left-s',
]);

interface SpriteIconProps {
    name: SpriteIconName;
    /** px, same for width and height */
    size?: number;
    /** Sprite icons only (the masked glyphs have it baked in) */
    strokeWidth?: number;
    className?: string;
}

export const SpriteIcon: React.FC<SpriteIconProps> = ({
    name,
    size = 24,
    strokeWidth,
    className,
}) => {
    if (MASKED.has(name)) {
        return (
            <span
                aria-hidden='true'
                className={`ks-i ks-i-${name}${className ? ` ${className}` : ''}`}
                style={{ width: size, height: size }}
            />
        );
    }
    return (
        <svg
            aria-hidden='true'
            width={size}
            height={size}
            className={className}
            fill='none'
            stroke='currentColor'
            strokeWidth={strokeWidth ?? 2}
            strokeLinecap='round'
            strokeLinejoin='round'
        >
            <use href={`#ks-${name}`} />
        </svg>
    );
};
